import { useEffect } from 'react';

import { css, keyframes } from '@emotion/css';

import { reduceMovimiento } from '#components/mobile/budget/ficha/AnimacionSuficiente';
import { color, movimiento, suave } from '#components/mobile/ui/tokens';

/**
 * «Copia hecha» al estilo Rumbo, sobre el anillo del contador: mientras el
 * anillo se vacía de vuelta a cero (transición del propio anillo), sale una
 * onda de agua doble, cuatro destellos de rosa de los vientos en los puntos
 * cardinales y se dibuja un ✓ verde que se asienta con el muelle de la
 * marca. Una vez (≈ 1,2 s), superpuesta y sin captar toques. Con «reducir
 * movimiento» solo un fundido breve del ✓. Dibujo propio (SVG + keyframes).
 */

export const DURACION_CELEBRACION = 1200;
const DURACION_REDUCIDA = 250;

const asentar = keyframes({
  '0%': { transform: 'scale(0.6)', opacity: 0 },
  '35%': { transform: 'scale(0.6)', opacity: 0 },
  '60%': { transform: 'scale(1.12)', opacity: 1 },
  '80%': { transform: 'scale(1)', opacity: 1 },
  '100%': { transform: 'scale(1)', opacity: 0 },
});

const dibujarCheck = keyframes({
  '0%, 38%': { strokeDashoffset: 1 },
  '64%, 100%': { strokeDashoffset: 0 },
});

const onda = keyframes({
  '0%': { transform: 'scale(0.85)', opacity: 0 },
  '12%': { transform: 'scale(1)', opacity: 0.6 },
  '100%': { transform: 'scale(2.2)', opacity: 0 },
});

const destello = keyframes({
  '0%, 20%': { transform: 'translateY(0) scale(0.3)', opacity: 0 },
  '40%': { transform: 'translateY(-4px) scale(1)', opacity: 1 },
  '100%': { transform: 'translateY(-12px) scale(0.5)', opacity: 0 },
});

const aparecer = keyframes({
  '0%': { opacity: 0 },
  '40%': { opacity: 1 },
  '100%': { opacity: 0 },
});

const sinMovimiento = css({
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { animation: 'none !important', opacity: '0 !important' },
  },
});

const CHECK = 'M20 6 9 17l-5-5';

export function CelebracionCopia({
  tamano,
  onFin,
}: {
  /** Lado del anillo (px); la capa ocupa el doble para que quepa la onda. */
  tamano: number;
  onFin: () => void;
}) {
  const reducir = reduceMovimiento();
  const duracion = reducir ? DURACION_REDUCIDA : DURACION_CELEBRACION;

  useEffect(() => {
    const id = window.setTimeout(onFin, duracion);
    return () => window.clearTimeout(id);
  }, [onFin, duracion]);

  const lado = tamano * 2;
  const c = lado / 2;
  const r = tamano / 2 - 2;
  const lCheck = tamano * 0.42;
  const capa = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: lado,
    height: lado,
    marginTop: -c,
    marginLeft: -c,
    pointerEvents: 'none',
    zIndex: 2,
    lineHeight: 0,
  } as const;

  const check = (estilo: object) => (
    <g
      transform={`translate(${c - lCheck / 2} ${c - lCheck / 2}) scale(${lCheck / 24})`}
      fill="none"
      stroke={color.ok}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={CHECK} pathLength={1} strokeDasharray="1 1" style={estilo} />
    </g>
  );

  if (reducir) {
    return (
      <span
        aria-hidden
        data-testid="copia-celebracion-reducida"
        style={{
          ...capa,
          animation: `${aparecer} ${DURACION_REDUCIDA}ms ease-out both`,
        }}
      >
        <svg viewBox={`0 0 ${lado} ${lado}`} width={lado} height={lado}>
          {check({ strokeDashoffset: 0 })}
        </svg>
      </span>
    );
  }

  const ms = `${DURACION_CELEBRACION}ms`;
  return (
    <span
      aria-hidden
      data-testid="copia-celebracion"
      className={sinMovimiento}
      style={capa}
    >
      <svg
        viewBox={`0 0 ${lado} ${lado}`}
        width={lado}
        height={lado}
        style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
      >
        {[0, 220].map(retardo => (
          <circle
            key={retardo}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={color.ok}
            strokeWidth={1.6}
            style={{
              transformOrigin: `${c}px ${c}px`,
              animation: `${onda} ${ms} ${movimiento.suave} ${retardo}ms both`,
            }}
          />
        ))}
        {[0, 90, 180, 270].map((a, i) => (
          <g key={a} transform={`rotate(${a} ${c} ${c})`}>
            <path
              d={`M${c} ${c - r - 8}l2.4 5-2.4 5-2.4-5z`}
              fill={color.ok}
              style={{
                transformBox: 'fill-box',
                transformOrigin: '50% 100%',
                animation: `${destello} ${ms} ${movimiento.suave} ${i * 30}ms both`,
              }}
            />
          </g>
        ))}
        {[45, 135, 225, 315].map(a => (
          <g key={a} transform={`rotate(${a} ${c} ${c})`}>
            <circle
              cx={c}
              cy={c - r - 5}
              r={1.8}
              fill={suave(color.ok, 70)}
              style={{
                transformBox: 'fill-box',
                transformOrigin: '50% 50%',
                animation: `${destello} ${ms} ${movimiento.suave} 80ms both`,
              }}
            />
          </g>
        ))}
      </svg>
      <svg
        viewBox={`0 0 ${lado} ${lado}`}
        width={lado}
        height={lado}
        style={{
          position: 'absolute',
          inset: 0,
          transformOrigin: '50% 50%',
          animation: `${asentar} ${ms} ${movimiento.muelle} both`,
        }}
      >
        <circle cx={c} cy={c} r={r - 3} fill={color.okSoft} />
        {check({
          animation: `${dibujarCheck} ${ms} cubic-bezier(.6,0,.3,1) both`,
        })}
      </svg>
    </span>
  );
}
